import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Bio } from '../../data/constants'
import Typewriter from 'typewriter-effect'
import HeroImg from '../../images/HeroImage.jpeg'
import HeroBgAnimation from '../HeroBgAnimation'

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const glowPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.3), 
                0 0 40px rgba(0, 255, 255, 0.1),
                0 0 60px rgba(255, 0, 255, 0.05);
  }
  50% { 
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.5), 
                0 0 60px rgba(0, 255, 255, 0.2),
                0 0 80px rgba(255, 0, 255, 0.1);
  }
`;

const borderGlow = keyframes`
  0%, 100% { border-color: rgba(0, 255, 255, 0.5); }
  50% { border-color: rgba(255, 0, 255, 0.5); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const HeroContainer = styled.div`
  background: transparent;
  display: flex;
  justify-content: center;
  position: relative;
  padding: 80px 30px;
  z-index: 1;

  @media screen and (max-width: 960px) {
    padding: 66px 16px;
  }

  @media screen and (max-width: 640px) {
    padding: 32px 16px;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
`;

const HeroInnerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;

  @media screen and (max-width: 960px) {
    flex-direction: column;
  }
`;

const HeroLeftContainer = styled.div`
  width: 100%;
  order: 1;
  animation: ${fadeInUp} 0.8s ease-out;
  
  @media screen and (max-width: 960px) {
    order: 2;
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    flex-direction: column;
  }
`;

const HeroRightContainer = styled.div`
  width: 100%;
  order: 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  gap: 12px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s backwards;

  @media screen and (max-width: 960px) {
    order: 1;
    justify-content: center;
    align-items: center;
    margin-bottom: 80px;
  }

  @media screen and (max-width: 640px) {
    order: 1;
    margin-bottom: 30px;
  }
`;

const Greeting = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: block;
  font-family: 'JetBrains Mono', monospace;
  
  &::before {
    content: '< ';
    opacity: 0.7;
  }
  
  &::after {
    content: ' />';
    opacity: 0.7;
  }
  
  @media screen and (max-width: 640px) {
    font-size: 14px;
  }
`;

const Title = styled.div`
  font-size: 56px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.2;
  margin-bottom: 8px;

  @media screen and (max-width: 960px) {
    text-align: center;
  }

  @media screen and (max-width: 640px) {
    font-size: 40px;
    line-height: 48px;
    margin-bottom: 8px;
  }
`;

const TextLoop = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 68px;
  gap: 12px;
  display: flex;

  @media screen and (max-width: 960px) {
    text-align: center;
    justify-content: center;
  }

  @media screen and (max-width: 640px) {
    font-size: 22px;
    line-height: 48px;
    margin-bottom: 16px;
  }
`;

const Span = styled.span`
  background: ${({ theme }) => theme.gradient_text};
  background-size: 300% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
  cursor: pointer;
`;

const SubTitle = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.8;
  margin-bottom: 42px;
  max-width: 600px;

  @media screen and (max-width: 960px) {
    text-align: center;
  }
  
  @media screen and (max-width: 640px) {
    font-size: 16px;
    line-height: 28px;
  }
`;

const ResumeButton = styled.a`
  -webkit-appearance: button;
  -moz-appearance: button;
  appearance: button;
  text-decoration: none;
  width: 95%;
  max-width: 300px;
  text-align: center;
  padding: 16px 0;
  color: ${({ theme }) => theme.bg};
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.4s ease-in-out;
  background: ${({ theme }) => theme.gradient_primary};
  position: relative;
  overflow: hidden;
  border: none;
  letter-spacing: 1px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.gradient_primary};
    filter: blur(20px);
    opacity: 0;
    z-index: -1;
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: scale(1.05) translateY(-3px);
    box-shadow: 0 20px 40px ${({ theme }) => theme.shadow_primary};
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover::after {
    opacity: 0.6;
  }
  
  @media (max-width: 640px) {
    padding: 12px 0;
    font-size: 16px;
  } 
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: ${({ theme }) => theme.gradient_primary};
    filter: blur(60px);
    opacity: 0.3;
    animation: ${glowPulse} 4s ease-in-out infinite;
  }
  
  @media screen and (max-width: 768px) {
    &::before {
      width: 350px;
      height: 350px;
    }
  }
  
  @media screen and (max-width: 640px) {
    &::before {
      width: 280px;
      height: 280px;
    }
  }
`;

const Image = styled.img`
  position: relative;
  border-radius: 50%;
  width: 400px;
  height: 400px;
  object-fit: cover;
  object-position: center;
  z-index: 1;
  border: 3px solid transparent;
  background: linear-gradient(${({ theme }) => theme.card}, ${({ theme }) => theme.card}) padding-box,
              ${({ theme }) => theme.gradient_primary} border-box;
  animation: ${float} 6s ease-in-out infinite, ${borderGlow} 3s ease-in-out infinite;
  box-shadow: 0 0 30px ${({ theme }) => theme.shadow_primary}, 
              0 0 60px ${({ theme }) => theme.shadow_accent};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 0 40px ${({ theme }) => theme.shadow_primary}, 
                0 0 80px ${({ theme }) => theme.shadow_accent};
    transform: scale(1.02);
  }

  @media screen and (max-width: 768px) {
    width: 350px;
    height: 350px;
  }

  @media screen and (max-width: 640px) {
    width: 280px;
    height: 280px;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.glass_border};
  border-radius: 50px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  
  @media screen and (max-width: 960px) {
    margin: 0 auto 20px;
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.success};
  animation: ${glowPulse} 2s ease-in-out infinite;
`;

const StatusText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  font-weight: 500;
`;


const Hero = () => {
  return (
    <div id='about'>
      <HeroContainer>
        <HeroBg>
          <HeroBgAnimation />
        </HeroBg>
        <HeroInnerContainer>
          <HeroLeftContainer>
            <StatusBadge>
              <StatusDot />
              <StatusText>Available for opportunities</StatusText>
            </StatusBadge>
            <Greeting>Hello World</Greeting>
            <Title>Hi, I am<br />{Bio.name}</Title>
            <TextLoop>I am a <Span><Typewriter options={{
              strings: Bio.roles,
              autoStart: true,
              loop: true,
            }}
            />
            </Span>
            </TextLoop>
            <SubTitle>{Bio.description}</SubTitle>
            <ResumeButton href={Bio.resume} target="_blank">Check Resume</ResumeButton>
          </HeroLeftContainer>
          <HeroRightContainer>
            <ImageContainer>
              <Image src={HeroImg} alt="Sunny Mondal" />
            </ImageContainer>
          </HeroRightContainer>
        </HeroInnerContainer>
      </HeroContainer>
    </div>
  )
}

export default Hero